import { useCallback, useEffect, useRef, useState } from 'react'
import { styled } from '@mui/system'
import { ColDef, Column, GridApi, RowNode } from 'ag-grid-community'
import Checkbox from '../../../../../../../components/editors/checkbox/Checkbox'
import { addEventListenerForLastRow } from '../../../../index'
import { FunctionProperty } from '../../../../../../../../lib/commons/appFunction'
import ViewMeta from '../../../../../../meta/ViewMeta'
import * as validator from '../../../../lib/validator'
import { FontSize } from '../../../../../../../../styles/commonStyles'
import { Tooltip } from '@mui/material'
import { AG_GRID_THEME } from '../../../../../../BulkSheet/const'

// Styles
const RootDiv = styled('div')({
  width: '100%',
  height: '100%',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})
const TextLabel = styled('div')({
  justifyContent: 'flex-end',
})
const StyledCheckbox = styled(Checkbox)<{ hidden: boolean }>(({ hidden }) => {
  return {
    margin: '0 auto',
    justifyContent: 'center',
    display: hidden ? 'none' : undefined,
  }
})
const StyledTooltip = styled(Tooltip)({
  '& .MuiTooltip-tooltip': {
    fontSize: FontSize.MEDIUM,
    maxWidth: 465,
    padding: 10,
  },
})

interface Props {
  value: boolean | number | string
  node: RowNode
  colDef: ColDef
  api: GridApi
  column: Column
  gridApi: GridApi
  rowIndex: number

  field: string
  uiMeta: FunctionProperty
  viewMeta: ViewMeta
  onValueChange?: (value: any) => void
}

const isCheckboxValue = (value: boolean | number | string) => {
  return typeof value !== 'number' && typeof value !== 'string'
}

const CheckBoxCell = (props: Props) => {
  const [value, setValue] = useState<boolean | number | string>(props.value)
  const [message, setMessage] = useState<string | undefined>()
  const [openTooltip, setOpenTooltip] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  const validate = useCallback(
    (val: boolean | number | string) => {
      if (!isCheckboxValue(val)) return
      if (!props.uiMeta) return
      const msg = validator.validate(
        val,
        props.node.data,
        props.uiMeta,
        props.viewMeta,
        props.api
      )
      if (rootRef.current) {
        if (msg) {
          rootRef.current.style.color = 'red'
          rootRef.current.style.backgroundColor = validator.inputErrorColor
          setMessage(msg)
        } else {
          rootRef.current.style.color = ''
          rootRef.current.style.backgroundColor = ''
          setMessage(undefined)
        }
      }
    },
    [props.uiMeta, props.viewMeta, props.node.data, props.api]
  )

  useEffect(() => {
    const focusedCell = props.api.getFocusedCell()
    if (
      inputRef.current &&
      focusedCell &&
      focusedCell.rowIndex === props.rowIndex &&
      focusedCell.column.getColId() === props.column.getColId()
    ) {
      inputRef.current.focus()
    }
    if (rootRef.current) {
      validate(value)
      addEventListenerForLastRow(rootRef.current, props)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時のみ実行。セルエディタの初期化処理
  }, [])

  // 外部から props.value が変更された場合に同期
  useEffect(() => {
    if (props.value !== value) {
      setValue(props.value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- value を含めると双方向同期ループになる。props.value の変化のみ検知
  }, [props.value])

  const hide = () => {
    const data = props.node.data
    if (data && data.isTotal) return true
    const hideParam = props.colDef.cellEditorParams.hide
    if (typeof hideParam === 'function') return hideParam(props.node)
    return !!hideParam
  }

  const disabled = () => {
    const disabledParam = props.colDef.cellEditorParams.disabled
    if (typeof disabledParam === 'function') return disabledParam(props.node)
    return !!disabledParam
  }

  const handleChange = (checked: boolean) => {
    props.onValueChange?.(checked)
    setValue(checked)
    props.node.setDataValue(props.field, checked)
    validate(checked)
  }

  const getLabel = () => {
    if (value) {
      return props.colDef.cellRendererParams.labelTrue || ''
    }
    return props.colDef.cellRendererParams.labelFalse || ''
  }

  const handleSetOpenTooltip = (open: boolean) => {
    !!message && setOpenTooltip(open)
  }

  if (props.node.data.isViewOnly) {
    return <></>
  }

  return (
    <StyledTooltip title={message || ''} placement="top" open={openTooltip}>
      <RootDiv
        ref={rootRef}
        onMouseOver={() => handleSetOpenTooltip(true)}
        onMouseLeave={() => handleSetOpenTooltip(false)}
      >
        {!isCheckboxValue(value) && (
          <TextLabel className="sevend-ag-cell">{props.value}</TextLabel>
        )}
        {isCheckboxValue(value) && (
          <StyledCheckbox
            hidden={hide()}
            label={
              getLabel() && <span className={AG_GRID_THEME}>{getLabel()}</span>
            }
            value={value}
            checked={!!value}
            color="primary"
            disabled={disabled()}
            onChange={(_, checked) => handleChange(checked)}
            inputRef={inputRef}
          />
        )}
      </RootDiv>
    </StyledTooltip>
  )
}

export default CheckBoxCell
